import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { ICtaAnimationData, START_ANIMATION_CLASS } from './animations';
import { useCTA } from './hooks';

import { filterProps } from 'utils/helpers';

export interface ICTA {
  ctas: string[];
  animationTriggerElement: HTMLDivElement | null;
}

const Container = styled('div', filterProps('animationData'))<{
  animationData: Omit<ICtaAnimationData, 'delays'>;
}>`
  ${({
    animationData: {
      staggerAnimation,
      cursorAnimation,
      typewriterAnimation,
      finalStaggerAnimation,
      finalCursorAnimation,
      finalTypewriterAnimation,
      pulseAnimation,
      pulseAnimationDurationMs,
      baseAnimationMs,
      totalAnimationMs,
    },
  }) => css`
    display: grid;
    position: relative;
    justify-items: flex-start;

    & > * {
      opacity: 0;
    }

    & > :last-child {
      opacity: 1;

      &::after {
        animation: ${pulseAnimation} ${pulseAnimationDurationMs}ms infinite linear;
      }
    }

    &.${START_ANIMATION_CLASS} {
      & > :not(:last-child) {
        animation-name: ${staggerAnimation};
        animation-duration: ${totalAnimationMs}ms;

        &::after {
          animation: ${cursorAnimation} ${baseAnimationMs}ms infinite;
          animation-delay: ${baseAnimationMs}ms;
        }

        & > h5 {
          animation: ${typewriterAnimation} ${baseAnimationMs}ms alternate infinite;
          animation-delay: ${baseAnimationMs}ms;
        }
      }

      & > :last-child {
        opacity: 0;
        animation-name: ${finalStaggerAnimation};
        animation-duration: ${totalAnimationMs}ms;

        &::after {
          animation: ${finalCursorAnimation} ${totalAnimationMs}ms infinite linear;
          animation-delay: inherit;
        }

        & > h5 {
          animation: ${finalTypewriterAnimation} ${totalAnimationMs}ms infinite;
          animation-delay: inherit;
        }
      }
    }
  `}
`;

const TextWrapper = styled('div', filterProps('animationDelayMs'))<{
  animationDelayMs: number;
}>`
  ${({ theme: { colors }, animationDelayMs }) => css`
    display: inline-flex;
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
    align-items: center;
    animation-delay: ${animationDelayMs}ms;
    animation-iteration-count: infinite;

    &::after {
      display: block;
      background-color: ${colors.primary};
      width: 0.75rem;
      height: 3rem;
      content: '';
    }
  `}
`;

const Text = styled.h5`
  ${({ theme: { colors, mq, tp } }) => css`
    margin: 0;
    padding-right: 1rem;
    overflow-x: hidden;
    line-height: 1.17;
    white-space: nowrap;
    color: ${colors.primary};
    font-size: 3rem;

    ${tp.useFont(fonts => fonts.Switzer.bold)};

    ${mq.md} {
      line-height: 1.36;
    }
  `}
`;

const CTA: React.FC<ICTA> = ({ ctas, animationTriggerElement }) => {
  const { ctasRef, delays, animationData } = useCTA({
    ctas,
    animationTriggerElement,
  });

  return (
    <Container animationData={animationData} ref={ctasRef}>
      {ctas.map((cta, index) => (
        <TextWrapper key={`${cta}${index}`} animationDelayMs={delays[index]}>
          <Text>{cta}</Text>
        </TextWrapper>
      ))}
    </Container>
  );
};

export default CTA;
